import { FORM_ERROR } from "final-form";
import { Field, Form } from "react-final-form";
import { generatePath, useHistory } from "react-router-dom";

import { createSearch } from "@/api/endpoints/searches";
import { useAlert } from "@/contexts/AlertContext";
import { useOrganization } from "@/hooks/useOrganization";
import { ROUTES } from "@/routes_new";
import { CandidateSearch, UnsavedSearch } from "@/types/Search";
import { validateRequired } from "@/utils";
import Button from "@components/Button";
import { FF_InputField, FF_SelectField } from "@components/Field";
import Modal, {
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "@components/Modal";
import { ModalProps } from "@components/Modal/Modal";
import Typography from "@components/Typography";

import styles from "./CreateAlertModal.module.css";
import { i18n } from "@lingui/core";

const CreateAlertModal: React.FunctionComponent<
  Omit<ModalProps, "children"> & {
    search: UnsavedSearch;
  }
> = ({ search = {}, ...props }) => {
  const history = useHistory();
  const { refresh } = useAlert();
  const { data: orgData } = useOrganization();
  const { uuid: orgUuid } = orgData?.ok ? orgData.data : {};

  const onSubmit = (values: CandidateSearch) => {
    return createSearch(values).then(async (res) => {
      if (res.data && res.status === 201) {
        // Refresh the alert list
        await refresh();
        // Navigate to the new alert
        history.push(
          generatePath(ROUTES.MENTION_DETAIL_ORGID, {
            orgUuid,
            uuid: res.data.uuid,
          })
        );
      } else {
        if (!res.ok && res.data) {
          const errors: string[] = [];
          (res.data?.queries || []).forEach((query) => {
            if (query?.non_field_errors !== undefined) {
              if (Array.isArray(query.non_field_errors)) {
                errors.concat(
                  query.non_field_errors.filter(Boolean) as string[]
                );
              }
            }
          });

          if (errors.length > 0) {
            return { [FORM_ERROR]: errors.join(", ") };
          }
        }
      }
    });
  };

  if (!orgUuid) return null;

  return (
    <Modal {...props}>
      <Form onSubmit={onSubmit} initialValues={search}>
        {({
          handleSubmit,
          pristine,
          invalid,
          dirtySinceLastSubmit,
          submitting,
          submitError,
        }) => (
          <form onSubmit={handleSubmit}>
            <ModalHeader>
              <ModalTitle>{L.EXPLORE_NEW_ALERT}</ModalTitle>
            </ModalHeader>
            <ModalContent>
              {submitError && (
                <Typography className={styles.error}>{submitError}</Typography>
              )}
              <Field
                block
                label={L.EXPLORE_NEW_ALERT_FIELD_TITLE_LABEL}
                name="title"
                data-testid="alert-title-input"
                component={FF_InputField}
                placeholder={L.EXPLORE_NEW_ALERT_FIELD_TITLE_PLACEHOLDER}
                validate={validateRequired(i18n._(L.REQUIRED_FIELD_VALUE))}
              />
              <Field
                block
                name="search_type"
                data-testid="alert-type-select"
                label={L.EXPLORE_NEW_ALERT_FIELD_TYPE_LABEL}
                validate={validateRequired(i18n._(L.REQUIRED_FIELD_VALUE))}
                placeholder={L.EXPLORE_NEW_ALERT_FIELD_TYPE_PLACEHOLDER}
                component={FF_SelectField}
                options={[
                  { value: "brand", label: L.ALERT_TYPE_BRAND },
                  { value: "organization", label: L.ALERT_TYPE_ORGANIZATION },
                  { value: "industry", label: L.ALERT_TYPE_INDUSTRY },
                  { value: "competitor", label: L.ALERT_TYPE_COMPETITOR },
                  { value: "spokesperson", label: L.ALERT_TYPE_SPOKESPERSON },
                  { value: "topic", label: L.ALERT_TYPE_TOPIC },
                  { value: "campaign", label: L.ALERT_TYPE_CAMPAIGN },
                  { value: "other", label: L.ALERT_TYPE_OTHER },
                ]}
              />
              <Field
                block
                name="start_date"
                label={L.EXPLORE_NEW_ALERT_FIELD_START_LABEL}
                placeholder={L.EXPLORE_NEW_ALERT_FIELD_START_PLACEHOLDER}
                component={FF_InputField}
              />
              <Field
                block
                name="end_date"
                label={L.EXPLORE_NEW_ALERT_FIELD_END_LABEL}
                placeholder={L.EXPLORE_NEW_ALERT_FIELD_END_PLACEHOLDER}
                component={FF_InputField}
              />
              <Field
                block
                name="sorting"
                helperText={L.EXPLORE_NEW_ALERT_FIELD_SORTING_HELP}
                label={L.EXPLORE_NEW_ALERT_FIELD_SORTING_LABEL}
                defaultValue={10}
                increment={1}
                type="number"
                component={FF_InputField}
              />
            </ModalContent>
            <ModalFooter>
              <Button
                type="submit"
                data-testid="alert-create-button"
                disabled={
                  submitting ||
                  pristine ||
                  invalid ||
                  (submitError && !dirtySinceLastSubmit)
                }
              >
                {L.EXPLORE_NEW_ALERT_CREATE}
              </Button>
            </ModalFooter>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default CreateAlertModal;
